import { formatUnits } from "viem";

/**
 * Formats a bigint token amount back into a human-readable decimal string.
 * Handles trimming, precision, and avoids scientific notation.
 */
export function formatBigIntToString(
  value: bigint,
  decimals: number,
  options?: {
    maxFractionDigits?: number;
    minFractionDigits?: number;
  },
): string {
  const divisor = BigInt(10) ** BigInt(decimals);
  const integerPart = value / divisor;
  const fractionPart = value % divisor;

  let fractionString = fractionPart.toString().padStart(decimals, "0");

  // Trim or pad based on options
  if (options?.maxFractionDigits != null) {
    fractionString = fractionString.slice(0, options.maxFractionDigits);
  }
  if (options?.minFractionDigits != null) {
    while (fractionString.length < options.minFractionDigits) {
      fractionString += "0";
    }
  }

  // Remove trailing zeros
  fractionString = fractionString.replace(/0+$/, "");

  return fractionString.length > 0 ? `${integerPart.toString()}.${fractionString}` : integerPart.toString();
}

/**
 * Converts a smallest-unit amount taken straight off the wire into a decimal token amount.
 * Returns null rather than a wrong number when the value is not a smallest-unit amount.
 */
export function parseAtomicAmount(value: string, decimals: number): null | string {
  // Whole and non-negative, or it is not a smallest-unit amount. BigInt("") is 0n, which would
  // otherwise turn an empty value into a real zero.
  if (!/^\d+$/.test(value)) return null;
  return formatUnits(BigInt(value), decimals);
}

export const emptyTokenBalance = {
  atomic: null,
  decimals: null,
  formatted: null,
  symbol: null,
  truncated: null,
} as const;

export type TokenBalance = {
  atomic: bigint | null;
  decimals: null | number;
  formatted: null | string;
  symbol: null | string;
  truncated: null | string;
};

export function formatFeePercent(fee: bigint, maxDecimals = 6): string {
  const WAD = 10n ** 18n;
  const percent = (Number(fee) / Number(WAD)) * 100;
  return `${percent.toLocaleString(undefined, {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: 0,
  })}%`;
}

export function formatTokenBalance(
  value: bigint | null | undefined,
  token: { decimals: number; symbol: string } | null | undefined,
): TokenBalance {
  if (typeof value !== "bigint" || !token?.decimals) {
    return {
      atomic: null,
      decimals: null,
      formatted: null,
      symbol: null,
      truncated: null,
    };
  }

  const formatted = formatUnits(value, token.decimals);

  return {
    atomic: value,
    decimals: token.decimals,
    formatted,
    symbol: token.symbol,
    truncated: truncateFormattedNumber(formatted),
  };
}

function truncateFormattedNumber(value: string, digits = 4): string {
  const [whole, frac = ""] = value.split(".");
  const trimmed = frac.slice(0, digits).replace(/0+$/, "");
  return trimmed ? `${whole}.${trimmed}` : whole;
}
